import { Col, FormInstance, Space, Typography } from 'antd';
import { isEmpty } from 'lodash';
import { Moment } from 'moment';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { VisibilityType } from 'api/project';
import { Field, FieldOption, StatusGroup } from 'api/tasks';
import { ReactComponent as RowConnectorSvg } from 'assets/svgs/row-connector.svg';
import { DatePicker } from 'components/atoms/date-picker';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Select } from 'components/atoms/select';
import { ProjectStatusSelect } from 'components/molecules/projects/project-status/project-status-select';
import { StepOne as TemplateList } from 'components/organisms/projects/project-plan/import-from-template/steps/step-one';
import { DueDateFormItem } from 'components/organisms/projects/project-plan/import-from-template/steps/step-one/form-items';
import { ImportFromTemplateFormValues } from 'components/organisms/projects/project-plan/import-from-template/types';
import { BodyShort01, ProductiveHeading07 } from 'components/styles';
import { usePublishedProjectTemplateOptionsQuery } from 'queries/templates/project-templates';
import {
  disabledDueDate,
  disabledStartDate,
} from 'utils/date-field/disabled-dates';
import { CompanyCreatableSelect } from './company-creatable-select';
import { Radio, Row, Title } from './styled';
import {
  CompanyOption,
  ProjectData,
  ProjectTemplateSourceOption,
} from './types';

const SubHeading = styled.h6`
  ${ProductiveHeading07};
`;
const Hint = styled.div`
  ${BodyShort01};
  color: var(--coolest-gray-100);
`;
const StyledRow = styled(Row)`
  margin-top: var(--spacing-07);
`;

export interface ProjectInfoStepFormValues
  extends ImportFromTemplateFormValues {
  projectName: string;
  customer?: CompanyOption;
  startDate?: Moment;
  dueDate?: Moment;
  source?: ProjectTemplateSourceOption;
  status?: { [statusFieldId: string]: FieldOption<StatusGroup> };
  visibility: VisibilityType;
}

interface ProjectInfoStepProps {
  form: FormInstance<ProjectInfoStepFormValues>;
  onValuesChange: (values: any, allValues: unknown) => void;
  statusField: Field;
  projectData: ProjectData;
}
export const ProjectInfoStep: FC<ProjectInfoStepProps> = ({
  form,
  onValuesChange,
  statusField,
  projectData,
}) => {
  const { data: templates, isLoading: isTemplatesLoading } =
    usePublishedProjectTemplateOptionsQuery();

  const hasTemplate = Boolean(form.getFieldValue('source'));
  const hasSources = (form.getFieldValue('sources') ?? []).length > 0;

  return (
    <Form
      layout="vertical"
      form={form}
      name="projectInfoStepForm"
      onValuesChange={onValuesChange}
      initialValues={{
        status: {
          [statusField.fieldId]: (statusField?.metaData?.options ?? []).find(
            (o) => o.value === statusField.metaData?.default,
          ),
        },
        visibility: projectData.visibility,
      }}
    >
      <Form.Item
        name="customer"
        label={
          <FormattedMessage id="projects.create.project_details.customer_name" />
        }
        rules={[
          {
            required: true,
            message: <FormattedMessage id="input.required" />,
          },
        ]}
      >
        <CompanyCreatableSelect form={form} />
      </Form.Item>
      <Row>
        <Col span={12}>
          <Form.Item
            id="projectStatus"
            name="projectName"
            label={
              <FormattedMessage id="projects.create.project_details.project_name" />
            }
            rules={[
              {
                required: true,
                message: <FormattedMessage id="input.required" />,
                whitespace: true,
              },
            ]}
          >
            <Input placeholder="Acme 2w project" autoComplete="off" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['status', statusField.fieldId.toString()]}
            label={<FormattedMessage id="project_status" />}
          >
            <ProjectStatusSelect />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <Form.Item
            name="source"
            label={
              <FormattedMessage id="projects.create.project_details.project_template" />
            }
            help={
              <FormattedMessage
                id="project.create.teamplates.admin.help"
                values={{
                  a: (chunks) => <Link to="/templates">{chunks}</Link>,
                }}
              />
            }
          >
            <Select
              options={templates}
              isClearable
              placeholder={
                <FormattedMessage id="projects.create.project_details.template" />
              }
              noOptionsMessage={({ inputValue }) => {
                const input = inputValue ? inputValue.trim() : undefined;

                if (isEmpty(templates) && !input) {
                  return <FormattedMessage id="no.options" />;
                }

                return (
                  <FormattedMessage
                    id="no.matches.found"
                    values={{
                      b: (chunks) => (
                        <Typography.Text strong>
                          {input ?? chunks}
                        </Typography.Text>
                      ),
                    }}
                  />
                );
              }}
              isLoading={isTemplatesLoading}
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 'var(--zindex-popover-low)',
                }),
              }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Row>
            <Col span={12}>
              <Form.Item
                name="startDate"
                required={hasTemplate}
                rules={[
                  {
                    required: hasTemplate,
                    message: <FormattedMessage id="input.required" />,
                  },
                ]}
                label={<FormattedMessage id="start_date" />}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD MMM YY"
                  allowClear
                  disabledDate={(current: Moment) =>
                    disabledStartDate(current, form.getFieldValue('dueDate'))
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              {hasTemplate ? (
                <DueDateFormItem
                  startDateFieldNamePath={['startDate']}
                  sourceFieldNamePath={['source']}
                />
              ) : (
                <Form.Item
                  name="dueDate"
                  label={<FormattedMessage id="due_date" />}
                  tooltip={
                    hasTemplate ? (
                      <FormattedMessage id="projects.create.due_date.duration" />
                    ) : null
                  }
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YY"
                    allowClear
                    disabledDate={(current: Moment) =>
                      disabledDueDate(current, form.getFieldValue('startDate'))
                    }
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {hasTemplate && hasSources && <RowConnectorSvg />}
      {hasTemplate && <TemplateList />}
      <StyledRow>
        <Col span={18}>
          <SubHeading>
            <FormattedMessage id="projects.create.team_visibility" />
          </SubHeading>
          <Form.Item name="visibility">
            <Radio.Group style={{ width: '100%' }}>
              <Radio value={VisibilityType.ALL}>
                <Space direction="vertical">
                  <Title>
                    <FormattedMessage id="projects.create.team_visibility.everyone" />
                  </Title>
                  <Space size={4} direction="vertical">
                    <Hint>
                      <FormattedMessage id="projects.create.team_visibility.everyone.extra" />
                    </Hint>
                    <Hint>
                      <FormattedMessage id="projects.create.team_visibility.everyone.extra1" />
                    </Hint>
                  </Space>
                </Space>
              </Radio>
              <Radio value={VisibilityType.INVITED}>
                <Space direction="vertical">
                  <Title>
                    <FormattedMessage id="projects.create.team_visibility.invited" />
                  </Title>
                  <Hint>
                    <FormattedMessage id="projects.create.team_visibility.invited.extra" />
                  </Hint>
                </Space>
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </StyledRow>
    </Form>
  );
};
